// Helpers that build event envelopes for POST /events.
//
// The shapes mirror what the projector validates (see
// meta/schemas/*.yaml + apps/projector/src/server.ts). Each builder
// returns an EventEnvelope ready to JSON-stringify and POST. The
// projector's response shape is uniform: { ok: true, ... } on
// success, { ok: false, error: { kind, ... } } on failure.

import { api } from '@rmint/shell';

type EventEnvelope = {
  entityType: 'subscription';
  entityId: string;
  eventId: string;
  eventType: string;
  occurredAt: string;
  signedBy: string;
  vars?: Record<string, string>;
  body: {
    subject: Record<string, string>;
    data: Record<string, unknown>;
  };
};

type ProjectorOk = {
  ok: true;
  idempotent: boolean;
  paths: Record<string, string>;
  state: { currentPhase: string; targetPhase: string };
  projectedPaths: string[];
  reviewId: string | null;
};

type ProjectorErr = {
  ok: false;
  error: { kind: string; reason?: string; [k: string]: unknown };
};

export type ProjectorResponse = ProjectorOk | ProjectorErr;

export async function postEvent(envelope: EventEnvelope): Promise<ProjectorResponse> {
  const res = await api.post('/events', envelope, { responseType: 'json' });
  return res.data as ProjectorResponse;
}

// ─── Builders ────────────────────────────────────────────────────

const nowIso = () => new Date().toISOString();
const newEvtId = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export type Decision = {
  subscriptionId: string;
  investorId: string;
  rmintId: string;
  officerId: string;
  reviewId: string;
};

export function kycOfficerAttested(d: Decision): EventEnvelope {
  return {
    entityType: 'subscription',
    entityId: d.subscriptionId,
    eventId: newEvtId('evt_attest'),
    eventType: 'kyc.officer_attested',
    occurredAt: nowIso(),
    signedBy: d.officerId,
    vars: { officerId: d.officerId },
    body: {
      subject: { investorId: d.investorId, rmintId: d.rmintId },
      data: {
        officerId: d.officerId,
        attestedAt: nowIso(),
        reviewId: d.reviewId,
      },
    },
  };
}

export function kycVerified(d: Decision): EventEnvelope {
  return {
    entityType: 'subscription',
    entityId: d.subscriptionId,
    eventId: newEvtId('evt_ver'),
    eventType: 'kyc.verified',
    occurredAt: nowIso(),
    signedBy: d.officerId,
    vars: { officerId: d.officerId },
    body: {
      subject: { investorId: d.investorId, rmintId: d.rmintId },
      data: {
        decisionId: newEvtId('dec'),
        officerId: d.officerId,
        validUntil: new Date(Date.now() + 365 * 86400_000).toISOString(),
        riskScore: 'low' as const,
      },
    },
  };
}

export function kycOfficerRejected(d: Decision & { reason: string }): EventEnvelope {
  return {
    entityType: 'subscription',
    entityId: d.subscriptionId,
    eventId: newEvtId('evt_rej_attest'),
    eventType: 'kyc.officer_rejected',
    occurredAt: nowIso(),
    signedBy: d.officerId,
    vars: { officerId: d.officerId },
    body: {
      subject: { investorId: d.investorId, rmintId: d.rmintId },
      data: {
        officerId: d.officerId,
        rejectedAt: nowIso(),
        reviewId: d.reviewId,
        reason: d.reason,
      },
    },
  };
}

export function kycRejected(d: Decision & { reason: string }): EventEnvelope {
  // Schema (meta/schemas/kyc-rejected.yaml):
  //   data: { decisionId, officerId, rejectionReason, rejectedAt }
  // Note `rejectionReason` (not `reason`) and `rejectedAt` (iso) —
  // the names diverge from kyc-officer-rejected because the lifecycle
  // event itself shouldn't reuse the attestation's field names.
  return {
    entityType: 'subscription',
    entityId: d.subscriptionId,
    eventId: newEvtId('evt_rej'),
    eventType: 'kyc.rejected',
    occurredAt: nowIso(),
    signedBy: d.officerId,
    vars: { officerId: d.officerId },
    body: {
      subject: { investorId: d.investorId, rmintId: d.rmintId },
      data: {
        decisionId: newEvtId('dec'),
        officerId: d.officerId,
        rejectionReason: d.reason,
        rejectedAt: nowIso(),
      },
    },
  };
}

export function kycSubmittedSeed(d: {
  subscriptionId: string;
  investorId: string;
  rmintId: string;
  officerId: string;
}): EventEnvelope {
  return {
    entityType: 'subscription',
    entityId: d.subscriptionId,
    eventId: newEvtId('evt_seed_kyc'),
    eventType: 'kyc.submitted',
    occurredAt: nowIso(),
    signedBy: d.investorId,
    vars: { officerId: d.officerId },
    body: {
      subject: { investorId: d.investorId, rmintId: d.rmintId },
      data: {
        stripeSessionId: `cs_seed_${Date.now()}`,
        documentTypes: ['passport'],
        submittedAt: nowIso(),
      },
    },
  };
}

// ─── Compound flows ──────────────────────────────────────────────

export async function approveKyc(d: Decision): Promise<ProjectorResponse> {
  // Step 1: officer attestation lands kyc-approval.yaml.
  const attest = await postEvent(kycOfficerAttested(d));
  if (!attest.ok) return attest;
  // Step 2: kyc.verified — guard now satisfied, lifecycle advances.
  return postEvent(kycVerified(d));
}

export async function rejectKyc(d: Decision & { reason: string }): Promise<ProjectorResponse> {
  const attest = await postEvent(kycOfficerRejected(d));
  if (!attest.ok) return attest;
  return postEvent(kycRejected(d));
}
